import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/** Eventos que contam como "alguém está usando o aparelho". */
const EVENTOS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll', 'mousemove'] as const;

/** Fim de sessão: o aparelho voltou a ficar livre para o próximo cliente. */
const EVT_REPOUSO = 'dispositivo:repouso';

/**
 * Avisa que o aparelho voltou ao repouso — pedido finalizado, sessão
 * abandonada, carrinho zerado pelo tempo limite. A proteção de tela volta na
 * hora, sem esperar o relógio de ociosidade: quem chega depois precisa
 * encontrar o convite, não o catálogo do cliente anterior.
 */
export function sinalizarRepouso(): void {
  window.dispatchEvent(new Event(EVT_REPOUSO));
}

/**
 * Quando a proteção de tela deve estar visível num aparelho da loja.
 *
 * Duas regras, e elas são independentes de propósito:
 *
 * 1. **Aparelho em repouso já mostra o convite**, como no totem de checkout.
 *    Ficar 60 segundos exibindo um catálogo parado não chama ninguém.
 *
 * 2. **Só um toque na proteção a dispensa.** Antes qualquer evento servia,
 *    inclusive `mousemove` — num totem com mouse ligado, ou com o dedo
 *    passando raspando, o convite caía sozinho e o aparelho voltava a exibir
 *    o catálogo para ninguém. Dispensar é decisão do cliente.
 *
 * O relógio de ociosidade continua ouvindo tudo, mas só ENQUANTO a proteção
 * está escondida — é o que decide quando ela volta.
 *
 * O gatilho de mostrar não é o `armado` virar verdadeiro, e sim o tempo desde
 * a última interação: no totem, abrir um produto e fechar sai e volta para o
 * catálogo, e a proteção pularia na cara de quem está no meio da escolha.
 *
 * @param armado se o aparelho está no estado em que a proteção faz sentido
 *   (ex.: no catálogo, sem pedido em andamento)
 * @param inatividadeMs tempo parado até a proteção voltar depois de dispensada
 */
export function useProtecaoOciosa(
  armado: boolean,
  inatividadeMs: number,
): { visivel: boolean; dispensar: () => void } {
  const [visivel, setVisivel] = useState(false);
  /** 0 = ninguém tocou nesta sessão — o aparelho está em repouso desde que ligou. */
  const ultimaInteracao = useRef(0);
  const timer = useRef<number | undefined>(undefined);

  const dispensar = useCallback(() => {
    ultimaInteracao.current = Date.now();
    setVisivel(false);
  }, []);

  // Fim de sessão: zera o relógio para a proteção voltar imediatamente.
  useEffect(() => {
    const aoRepousar = (): void => {
      ultimaInteracao.current = 0;
      if (armado) setVisivel(true);
    };
    window.addEventListener(EVT_REPOUSO, aoRepousar);
    return () => window.removeEventListener(EVT_REPOUSO, aoRepousar);
  }, [armado]);

  // Desarmada o cliente segue usando o aparelho (carrinho, pagamento): sem contar esses toques, a proteção pulava em quem acabou de voltar ao cardápio.
  useEffect(() => {
    if (armado) return;

    const anotar = (): void => {
      ultimaInteracao.current = Date.now();
    };
    for (const ev of EVENTOS) window.addEventListener(ev, anotar, { passive: true });
    return () => {
      for (const ev of EVENTOS) window.removeEventListener(ev, anotar);
    };
  }, [armado]);

  // Antes de pintar: aparelho que volta ao início já em repouso mostra a proteção direto, sem o cardápio piscar por baixo.
  useLayoutEffect(() => {
    window.clearTimeout(timer.current);

    if (!armado) {
      setVisivel(false);
      return;
    }
    // Visível: quem fecha é o toque na própria proteção, não um evento solto.
    if (visivel) return;

    const agendar = (ms: number): void => {
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setVisivel(true), ms);
    };

    const aoInteragir = (): void => {
      ultimaInteracao.current = Date.now();
      agendar(inatividadeMs);
    };

    const parado = Date.now() - ultimaInteracao.current;
    if (parado >= inatividadeMs) {
      setVisivel(true);
      return;
    }
    agendar(inatividadeMs - parado);

    for (const ev of EVENTOS) window.addEventListener(ev, aoInteragir, { passive: true });
    return () => {
      window.clearTimeout(timer.current);
      for (const ev of EVENTOS) window.removeEventListener(ev, aoInteragir);
    };
  }, [armado, visivel, inatividadeMs]);

  return { visivel, dispensar };
}
